<script setup lang="ts">
import UserBioPanel from '@/views/apps/user/view/UserBioPanel.vue'
import UserTabAccount from '@/views/apps/user/view/UserTabAccount.vue'
import UserTabPhysician from '@/views/apps/user/view/UserTabPhysician.vue'
import UserTabRelatives from '@/views/apps/user/view/UserTabRelatives.vue'
import UserTabNurse from '@/views/apps/user/view/UserTabNurse.vue'
import UserTabPharmacist from '@/views/apps/user/view/UserTabPharmacist.vue'
import UserTabPhysical from '@/views/apps/user/view/UserTabPhysical.vue'
import { UserProperties } from '@/views/apps/user/types'
import DiagnosisTab from '@/views/apps/user/view/medical/DiagnosisTab.vue'
import AllergiesTab from '@/views/apps/user/view/medical/AllergiesTab.vue'
import MedicalHistoryTab from '@/views/apps/user/view/medical/MedicalHistoryTab.vue'
import HospitalizationsTab from '@/views/apps/user/view/medical/HospitalizationsTab.vue'
import MedicationsTab from '@/views/apps/user/view/medical/MedicationsTab.vue'
import TreatmentsTab from '@/views/apps/user/view/medical/TreatmentsTab.vue'
import VaccinationTab from '@/views/apps/user/view/medical/VaccinationTab.vue'
import FallRiskTab from '@/views/apps/user/view/medical/FallRiskTab.vue'
import MedicalEquipmentTab from '@/views/apps/user/view/medical/MedicalEquipmentTab.vue'
import MemoryTab from '@/views/apps/user/view/cognitive/MemoryTab.vue'
import ExecutiveFunctionTab from '@/views/apps/user/view/cognitive/ExecutiveFunctionTab.vue'
import DailyLivingTab from '@/views/apps/user/view/cognitive/DailyLivingTab.vue'
import LanguageTab from '@/views/apps/user/view/cognitive/LanguageTab.vue'
import VisualTab from '@/views/apps/user/view/cognitive/VisualTab.vue'
import BehaviorTab from '@/views/apps/user/view/cognitive/BehaviorTab.vue'
import MotorFunctionTab from '@/views/apps/user/view/cognitive/MotorFunctionTab.vue'
import InterestsTab from '@/views/apps/user/view/cognitive/InterestsTab.vue'
import ClinicsTab from '@/views/apps/user/view/service-providers/ClinicsTab.vue'
import OrganizationsTab from '@/views/apps/user/view/service-providers/OrganizationsTab.vue'
import HospiceTab from '@/views/apps/user/view/service-providers/HospiceTab.vue'
import FinancialTab from '@/views/apps/user/view/finance/FinancialTab.vue'
import InsuranceTab from '@/views/apps/user/view/finance/InsuranceTab.vue'
import LegalTab from '@/views/apps/user/view/legal/LegalTab.vue'
import MarTab from '@/views/apps/user/view/legal/MarTab.vue'
import TarTab from '@/views/apps/user/view/legal/TarTab.vue'
import HousingTab from '@/views/apps/user/view/housing/HousingTab.vue'
import NotesAttachmentsTab from '@/views/apps/user/view/notes/NotesAttachmentsTab.vue'
import UserTabPrivacy from '@/views/apps/user/view/UserTabPrivacy.vue'
import UserTabContacts from '@/views/apps/user/view/UserTabContacts.vue'
import UserTabEnvironmental from '@/views/apps/user/view/cognitive/SocialTab.vue'

// DAY-37: only for users whose roles allow it (same permission the API checks)
definePage({
  meta: {
    action: 'read',
    subject: 'Participants',
  },
})

const { t } = useI18n()

const componentRegistry = {
  UserTabEnvironmental,
  UserTabContacts,
  UserTabAccount,
  FallRiskTab,
  AllergiesTab,
  DiagnosisTab,
  MedicalHistoryTab,
  HospitalizationsTab,
  UserTabPhysician,
  UserTabRelatives,
  UserTabNurse,
  UserTabPharmacist,
  UserTabPhysical,
  MedicationsTab,
  TreatmentsTab,
  VaccinationTab,
  MedicalEquipmentTab,
  MemoryTab,
  ExecutiveFunctionTab,
  DailyLivingTab,
  LanguageTab,
  VisualTab,
  BehaviorTab,
  MotorFunctionTab,
  InterestsTab,
  ClinicsTab,
  OrganizationsTab,
  HospiceTab,
  FinancialTab,
  InsuranceTab,
  LegalTab,
  MarTab,
  TarTab,
  HousingTab,
  NotesAttachmentsTab,
  UserTabPrivacy,
}

interface MenuChild {
  title: string
}

interface MenuItem {
  icon: string
  title: string
  children?: MenuChild[]
}

interface MenuTab {
  title: string
  data: MenuItem[]
}

interface PatientMenuItem {
  id: number
  patientMenuItemId: number
  level: number
  title: string
  key: string
  icon: string | null
  position: number
  isActive: boolean
  children: PatientMenuItem[]
}

const route = useRoute('apps-user-view-id')

const userTab = ref(null)

const { data: menuData } = await useApi<PatientMenuItem[]>(`/PatientMenu/GetCurrentUserMenu`)
const { data: userData } = await useApi<any>(`/Member/GetMember/${route.params.id}`)

const onUpdateUser = async (data: UserProperties) => {
  // Handle the submitted data
  try {
    await $api(`/Member/UpdateMember/${route.params.id}`, {
      method: 'PUT',
      body: data,
    })
    userData.value = data
  } catch (error) {
    console.error('Error updating user info:', error)
  }
}

const allTabChildren = computed(() => menuData.value?.flatMap((tab) => tab.children) || [])

// When the users-list "Edit" action navigates here with ?edit=all, force the
// Account tab so UserTabAccount actually mounts and can open its Edit-All dialog.
watch(
  () => [route.query.edit, allTabChildren.value],
  () => {
    if (route.query.edit !== 'all') return
    const idx = allTabChildren.value.findIndex((c: any) => c.key === 'UserTabAccount')
    if (idx >= 0) userTab.value = idx as any
  },
  { immediate: true },
)
</script>

<template>
  <div v-if="userData">
    <div class="d-flex align-center gap-x-4 mb-6">
      <VBtn
        variant="tonal"
        color="secondary"
        :to="{ name: 'apps-user-list' }"
      >
        <VIcon icon="bx-arrow-back" class="me-2" />
        {{ t('Back to Users') }}
      </VBtn>
      <h4 class="text-h4">
        {{ t('User Details') }}
      </h4>
    </div>

  <VRow>
    <VCol cols="12" md="8" lg="9">
      <VCard>
        <VRow>
          <VCol cols="12" md="4" :class="$vuetify.display.smAndDown ? 'border-b' : 'border-e'">
            <VCardText>
              <VTabs v-model="userTab" class="v-tabs-pill" direction="vertical">
                <template v-for="tab in menuData" :key="tab.title">
                  <h3>{{ t(tab.title) }}</h3>
                  <VTab v-for="{ title, icon } in tab.children" :key="title">
                    <VIcon v-if="icon" :size="18" :icon="icon" class="me-1" />
                    <span>{{ t(title) }}</span>
                  </VTab>
                </template>
              </VTabs>
            </VCardText>
          </VCol>

          <VCol cols="12" md="8">
            <VCardText class="px-2 px-md-4">
              <VWindow v-model="userTab" class="disable-tab-transition">
                <VWindowItem v-for="{ key, title } in allTabChildren" :key="key">
                  <UserTabAccount v-if="key === 'UserTabAccount'" :data="userData" @update-user="onUpdateUser" />
                  <component
                    v-else-if="componentRegistry[key as keyof typeof componentRegistry]"
                    :is="componentRegistry[key as keyof typeof componentRegistry]" />
                  <VCard v-else class="text-center">{{ t('No component found for:') }} {{ t(title) }}</VCard>
                </VWindowItem>
              </VWindow>
            </VCardText>
          </VCol>
        </VRow>
      </VCard>
    </VCol>
    <VCol cols="12" md="4" lg="3">
      <UserBioPanel :user-data="userData" @update-user="onUpdateUser" />
    </VCol>
  </VRow>
  </div>
  <div v-else>
    <VAlert type="error" variant="tonal"> Member with ID {{ route.params.id }} not found! </VAlert>
  </div>
</template>
